import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  BarChart3, TrendingUp, Users, Building, Sparkles, Eye, UserPlus,
  Monitor, Smartphone, Tablet, Loader2, RefreshCw, Calendar,
  ArrowUpRight, ArrowDownRight, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component
const SimpleBarChart = ({ data, dataKey, labelKey, color = 'bg-red-500', maxBars = 10 }) => {
  if (!data || data.length === 0) return <p className="text-neutral-500 text-sm">No data available</p>;
  
  const displayData = data.slice(0, maxBars);
  const maxValue = Math.max(...displayData.map(d => d[dataKey]));
  
  return (
    <div className="space-y-2">
      {displayData.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <div className="w-32 truncate text-sm text-neutral-400" title={item[labelKey]}>
            {item[labelKey] || 'Unknown'}
          </div>
          <div className="flex-1 h-6 bg-neutral-800 rounded-lg overflow-hidden">
            <div 
              className={`h-full ${color} transition-all duration-500`}
              style={{ width: `${(item[dataKey] / maxValue) * 100}%` }}
            />
          </div>
          <div className="w-12 text-right text-sm font-mono text-white">
            {item[dataKey].toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

// Trend line mini chart
const TrendLine = ({ data, valueKey }) => {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => d[valueKey]);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 100 - ((v - min) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" className="w-24 h-8">
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="text-red-500"
      />
    </svg>
  );
};

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics?days=${days}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
        <p className="text-neutral-400">Failed to load analytics</p>
        <p className="text-sm text-neutral-500 mt-2">Make sure you are logged in as an admin.</p>
        <Button onClick={fetchAnalytics} className="mt-4">Retry</Button>
      </div>
    );
  }

  // Provide defaults for all nested properties to prevent undefined errors
  const summary = analytics.summary || {
    total_events: 0,
    unique_visitors: 0,
    homepage_visits: 0,
    businesses_page_views: 0,
    sidequests_page_views: 0,
    business_views: 0,
    sidequest_views: 0,
    signups: 0
  };
  
  const visitors = analytics.visitors || {
    authenticated: 0,
    anonymous: 0,
    viewed_without_signup: 0
  };
  
  const devices = analytics.devices || {
    desktop: 0,
    mobile: 0,
    tablet: 0
  };
  
  const conversion_rates = analytics.conversion_rates || {
    homepage_to_sidequests: 0,
    homepage_to_businesses: 0,
    homepage_to_signup: 0
  };
  
  const popular_businesses = analytics.popular_businesses || [];
  const popular_sidequests = analytics.popular_sidequests || [];
  const daily_trends = analytics.daily_trends || [];
  const top_referrers = analytics.top_referrers || [];

  // Format conversion rate display
  const formatPercent = (val) => val ? `${val.toFixed(1)}%` : '0%';

  // Calculate trend (compare last 7 days to previous 7 days if we have enough data)
  const getTrend = (values) => {
    if (!values || values.length < 14) return null;
    const recent = values.slice(-7).reduce((a, b) => a + b, 0);
    const previous = values.slice(-14, -7).reduce((a, b) => a + b, 0);
    if (previous === 0) return null;
    return ((recent - previous) / previous * 100).toFixed(1);
  };

  const homepageTrend = getTrend(daily_trends?.map(d => d.homepage_visits));
  const signupTrend = getTrend(daily_trends?.map(d => d.signups));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Analytics</h1>
          <p className="text-neutral-400 mt-1">Track visitor behavior and conversions</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={fetchAnalytics}
            className="border-white/20 text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          label="Homepage Visits" 
          value={summary.homepage_visits} 
          icon={Eye}
          color="text-red-400"
          trend={homepageTrend}
          trendData={daily_trends}
          trendKey="homepage_visits"
        />
        <StatCard 
          label="Explore Sidequests" 
          value={summary.sidequests_page_views} 
          icon={Sparkles}
          color="text-purple-400"
          subtext={`${formatPercent(conversion_rates.homepage_to_sidequests)} of visitors`}
        />
        <StatCard 
          label="Discover Businesses" 
          value={summary.businesses_page_views} 
          icon={Building}
          color="text-blue-400"
          subtext={`${formatPercent(conversion_rates.homepage_to_businesses)} of visitors`}
        />
        <StatCard 
          label="User Signups" 
          value={summary.signups} 
          icon={UserPlus}
          color="text-green-400"
          trend={signupTrend}
          subtext={`${formatPercent(conversion_rates.homepage_to_signup)} conversion`}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          label="Unique Visitors" 
          value={analytics.unique_visitors || 0} 
          icon={Users}
          color="text-amber-400"
        />
        <StatCard 
          label="Business Page Views" 
          value={summary.business_views} 
          icon={Building}
          color="text-cyan-400"
        />
        <StatCard 
          label="Sidequest Page Views" 
          value={summary.sidequest_views} 
          icon={Sparkles}
          color="text-pink-400"
        />
        <StatCard 
          label="Total Events" 
          value={summary.total_events} 
          icon={TrendingUp}
          color="text-emerald-400"
        />
      </div>

      {/* Visitors Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Auth vs Anonymous */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-red-400" />
            Visitor Authentication
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{visitors.authenticated.toLocaleString()}</p>
              <p className="text-sm text-neutral-400">Logged In</p>
            </div>
            <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">{visitors.anonymous.toLocaleString()}</p>
              <p className="text-sm text-neutral-400">Anonymous</p>
            </div>
          </div>
          <div className="text-sm text-neutral-500">
            <span className="text-amber-400 font-medium">{visitors.viewed_without_signup.toLocaleString()}</span> views from users who haven't signed up
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-blue-400" />
            Device Breakdown
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
              <Monitor className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{devices.desktop.toLocaleString()}</p>
              <p className="text-sm text-neutral-400">Desktop</p>
            </div>
            <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
              <Smartphone className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{devices.mobile.toLocaleString()}</p>
              <p className="text-sm text-neutral-400">Mobile</p>
            </div>
            <div className="bg-neutral-900/50 rounded-xl p-4 text-center">
              <Tablet className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{devices.tablet.toLocaleString()}</p>
              <p className="text-sm text-neutral-400">Tablet</p>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Content */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Popular Businesses */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-cyan-400" />
            Most Viewed Businesses
          </h3>
          {popular_businesses.length > 0 ? (
            <SimpleBarChart 
              data={popular_businesses} 
              dataKey="views" 
              labelKey="name"
              color="bg-cyan-500"
            />
          ) : (
            <p className="text-neutral-500 text-sm">No business views recorded yet</p>
          )}
        </div>

        {/* Popular Sidequests */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-400" />
            Most Viewed Sidequests
          </h3>
          {popular_sidequests.length > 0 ? (
            <SimpleBarChart 
              data={popular_sidequests} 
              dataKey="views" 
              labelKey="title"
              color="bg-pink-500"
            />
          ) : (
            <p className="text-neutral-500 text-sm">No sidequest views recorded yet</p>
          )}
        </div>
      </div>

      {/* Daily Trends */}
      {daily_trends && daily_trends.length > 0 && (
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            Daily Activity Trend
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-400">Homepage</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-400">Business Views</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-400">Sidequest Views</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-400">Signups</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-neutral-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {daily_trends.slice(-14).reverse().map((day, idx) => (
                  <tr key={day.date} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 text-white">
                      {format(parseISO(day.date), 'MMM d, yyyy')}
                    </td>
                    <td className="py-3 px-4 text-right text-red-400 font-mono">{day.homepage_visits}</td>
                    <td className="py-3 px-4 text-right text-cyan-400 font-mono">{day.business_views}</td>
                    <td className="py-3 px-4 text-right text-pink-400 font-mono">{day.sidequest_views}</td>
                    <td className="py-3 px-4 text-right text-green-400 font-mono">{day.signups}</td>
                    <td className="py-3 px-4 text-right text-white font-mono">{day.total_events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Referrers */}
      {top_referrers && top_referrers.length > 0 && (
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-amber-400" />
            Top Referrers
          </h3>
          <SimpleBarChart 
            data={top_referrers.map(r => ({
              referrer: new URL(r.referrer).hostname || r.referrer,
              count: r.count
            }))} 
            dataKey="count" 
            labelKey="referrer"
            color="bg-amber-500"
          />
        </div>
      )}

      {/* Conversion Funnel Summary */}
      <div className="bg-gradient-to-br from-red-600/20 to-purple-600/20 rounded-2xl border border-red-500/30 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Conversion Funnel</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FunnelStep 
            label="Homepage" 
            value={summary.homepage_visits} 
            percentage={100}
            color="text-red-400"
          />
          <FunnelStep 
            label="→ Sidequests" 
            value={summary.sidequests_page_views} 
            percentage={conversion_rates.homepage_to_sidequests}
            color="text-purple-400"
          />
          <FunnelStep 
            label="→ Businesses" 
            value={summary.businesses_page_views} 
            percentage={conversion_rates.homepage_to_businesses}
            color="text-blue-400"
          />
          <FunnelStep 
            label="→ Signup" 
            value={summary.signups} 
            percentage={conversion_rates.homepage_to_signup}
            color="text-green-400"
          />
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
const StatCard = ({ label, value = 0, icon: Icon, color, trend, subtext, trendData, trendKey }) => (
  <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <p className="text-sm text-neutral-400">{label}</p>
      </div>
      {trendData && trendKey && (
        <TrendLine data={trendData} valueKey={trendKey} />
      )}
    </div>
    <div className="flex items-end justify-between">
      <p className={`text-3xl font-bold ${color}`}>{(value || 0).toLocaleString()}</p>
      {trend && (
        <div className={`flex items-center text-sm ${parseFloat(trend) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {parseFloat(trend) >= 0 ? (
            <ArrowUpRight className="w-4 h-4" />
          ) : (
            <ArrowDownRight className="w-4 h-4" />
          )}
          {Math.abs(parseFloat(trend))}%
        </div>
      )}
    </div>
    {subtext && <p className="text-xs text-neutral-500 mt-1">{subtext}</p>}
  </div>
);

// Funnel Step Component
const FunnelStep = ({ label, value = 0, percentage = 0, color }) => (
  <div className="text-center">
    <p className={`text-2xl font-bold ${color}`}>{(value || 0).toLocaleString()}</p>
    <p className="text-sm text-neutral-400 mt-1">{label}</p>
    <Badge className="mt-2 bg-white/10 text-white">
      {(percentage || 0).toFixed(1)}%
    </Badge>
  </div>
);
